import {AddIcon, MinusIcon} from '@chakra-ui/icons';
import {IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,} from '@chakra-ui/react';
import React, {useMemo} from 'react';
import {Row} from 'react-table';
import {useUpdatePlayerStatsMutation} from '../../../app/api';
import {BasketballLoaderOverlay} from '../../../components/BasketballLoader/BasketballLoaderOverlay';
import {YCTable} from '../../../components/YCTable/YCTable';
import {useEventStatsConfig} from '../../../hooks/useEventStatsConfig';
import {PlayerStats, STAT_KEYS, STAT_LABELS, StatKey, UpdatePlayerStats} from '../../../types/dto';

interface Data {
  name: string;
  value: number;
  id: StatKey;
}

interface PlayerScoringModalProps {
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  selectedPlayer?: PlayerStats | null;
  refetchGameStats?: () => void;
}

export const PlayerScoringModal: React.FC<PlayerScoringModalProps> = ({
                                                                        isOpen,
                                                                        onClose,
                                                                        selectedPlayer,
                                                                        refetchGameStats,
                                                                        isLoading,
                                                                      }) => {
  const [updatePlayerStats, {isLoading: updatePlayerStatsLoading}] =
    useUpdatePlayerStatsMutation();
  const showLoading = isLoading || updatePlayerStatsLoading;
  const {isStatEnabled, enabled} = useEventStatsConfig();

  const onStatsChange = useMemo(
    () =>
      (id: number, updated: PlayerStats) => {
        const stats: UpdatePlayerStats = {};
        STAT_KEYS.forEach((k) => {
          if (enabled.has(k)) {
            stats[k] = updated[k];
          }
        });
        updatePlayerStats({id, stats}).then(() => {
          if (refetchGameStats) {
            refetchGameStats();
          }
        });
      },
    [updatePlayerStats, refetchGameStats, enabled]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        id: 'name',
      },
      {
        Header: 'Remove',
        accessor: 'name',
        id: 'remove',
        Cell: ({row}: { row: Row<Data> }) => (
          <IconButton
            aria-label="Remove"
            disabled={(selectedPlayer as any)[row.original.id] === 0}
            icon={<MinusIcon/>}
            onClick={() => {
              onStatsChange(selectedPlayer!.id, {
                ...selectedPlayer!,
                [row.original.id]: (selectedPlayer as any)[row.original.id] - 1,
              });
            }}
          />
        ),
      },
      {
        Header: 'Value',
        accessor: 'value',
      },
      {
        Header: 'Add',
        accessor: 'name',
        id: 'add',
        Cell: ({row}: { row: Row<Data> }) => (
          <IconButton
            aria-label="Add"
            icon={<AddIcon/>}
            onClick={() => {
              onStatsChange(selectedPlayer!.id, {
                ...selectedPlayer!,
                [row.original.id]: (selectedPlayer as any)[row.original.id] + 1,
              });
            }}
          />
        ),
      },
    ],
    [selectedPlayer, onStatsChange]
  );

  const data: Data[] = STAT_KEYS.filter(isStatEnabled).map((key) => ({
    name: STAT_LABELS[key].short,
    value: selectedPlayer ? (selectedPlayer as any)[key] : 0,
    id: key,
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>{selectedPlayer?.name}</ModalHeader>
        <ModalCloseButton disabled={showLoading}/>
        <BasketballLoaderOverlay show={showLoading}/>
        <ModalBody>
          <YCTable columns={columns} data={data}/>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
